import * as React from 'react';
import type {AddressFieldSchema} from './schemaTypes';

// eslint-disable-next-line @typescript-eslint/consistent-type-imports
export type AddressCountryCode = keyof typeof import('./addressSchemas.json');
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
export type AddressCountryLabels = typeof import('./countryLabels.json');
export type AddressSchemas = Record<AddressCountryCode, AddressFieldSchema>;
export type AddressData = {
  addressSchemas: AddressSchemas;
  countryLabels: AddressCountryLabels;
  countryCodes: AddressCountryCode[];
};

const loadAddressSchemas = (): Promise<AddressSchemas> =>
  import('./addressSchemas.json').then((mod) => mod.default as AddressSchemas);
const loadCountryLabels = (): Promise<AddressCountryLabels> =>
  import('./countryLabels.json').then((mod) => mod.default);
const loadAddressData = async (): Promise<AddressData> => {
  const [addressSchemas, countryLabels] = await Promise.all([
    loadAddressSchemas(),
    loadCountryLabels(),
  ]);

  return {
    addressSchemas,
    countryLabels,
    countryCodes: Object.keys(addressSchemas) as AddressCountryCode[],
  };
};

let addressData: AddressData | undefined;

export function useAddressData(): {data?: AddressData; loading: boolean} {
  const [data, setData] = React.useState<AddressData | undefined>(addressData);
  const [loading, setLoading] = React.useState(!addressData);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    // If data is already loaded, do nothing
    if (addressData) {
      return;
    }

    loadAddressData()
      .then((data) => {
        addressData = data;
        setData(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, []);

  if (error) {
    throw error;
  }

  return {data, loading};
}
